import React, { useCallback, useEffect, useRef, useState } from 'react';
import FieldPickerPopup from './FieldPickerPopup';
import useSelectedFields from './useSelectedFields';
import useDataTypeOptions from './useDataTypeOptions';
import useFieldOptions from './useFieldOptions';

const FieldPickerPopupContainer = ({
  initialFields,
  onFieldsConfirmed,
  close,
  datasetsGreenList,
  fieldRedList,
  fieldTypeRedList,
  alwaysAllowedFields,
  isPerformanceDatasetsAllowed,
  isSaving,
  hideDatasetSidebar,
  showClearButton,
  applyOnChange,
}: {
  initialFields: {
    dataType: string;
    fields: string[];
  }[];
  onFieldsConfirmed: (
    confirmedFields: { dataType: string; fields: string[] }[],
  ) => void;
  close: () => void;
  datasetsGreenList?: string[];
  fieldRedList?: string[];
  fieldTypeRedList?: FleetOps.FieldType[];
  alwaysAllowedFields?: { dataType: string; fields: string[] }[];
  isPerformanceDatasetsAllowed?: boolean;
  isSaving?: boolean;
  hideDatasetSidebar?: boolean;
  showClearButton?: boolean;
  // When true, changes apply immediately (no "Confirm Selection" step).
  applyOnChange?: boolean;
}) => {
  const [isConfirmEnabled, setIsConfirmEnabled] = useState<boolean>(false);
  const {
    selectedFields,
    selectedFieldsCount,
    onFieldAdded,
    onFieldRemoved,
    onClearAll,
  } = useSelectedFields(initialFields);
  const { selectedDataType, dataTypeOptions, allowedDataTypes } =
    useDataTypeOptions({
      datasetsGreenList,
      isPerformanceDatasetsAllowed,
      alwaysAllowedFields,
    });
  const { searchText, onSearchTextChanged, fieldOptions } = useFieldOptions({
    allowedDataTypes,
    selectedDataType,
    selectedFields,
    fieldRedList,
    fieldTypeRedList,
    datasetsGreenList,
    alwaysAllowedFields,
  });
  const onConfirmClicked = useCallback(() => {
    onFieldsConfirmed(selectedFields);
    close();
  }, [close, onFieldsConfirmed, selectedFields]);

  // In applyOnChange mode, push the current selection to the consumer on every
  // change (skipping the initial mount) so the result updates live.
  const onFieldsConfirmedRef = useRef(onFieldsConfirmed);
  useEffect(() => {
    onFieldsConfirmedRef.current = onFieldsConfirmed;
  }, [onFieldsConfirmed]);
  const hasMountedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!applyOnChange) {
      return;
    }
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    onFieldsConfirmedRef.current(selectedFields);
  }, [applyOnChange, selectedFields]);

  // setIsConfirmEnabled
  useEffect(() => {
    setIsConfirmEnabled(
      initialFields.length > 0 ||
        (initialFields.length === 0 && selectedFieldsCount > 0),
    );
  }, [initialFields.length, selectedFieldsCount]);

  const onCancelClicked = useCallback(() => {
    close();
  }, [close]);

  return (
    <FieldPickerPopup
      selectedFieldsCount={selectedFieldsCount}
      onFieldAdded={onFieldAdded}
      onFieldRemoved={onFieldRemoved}
      dataTypeOptions={dataTypeOptions}
      searchText={searchText}
      onSearchTextChanged={onSearchTextChanged}
      fieldOptions={fieldOptions}
      isConfirmEnabled={isConfirmEnabled}
      onConfirmClicked={onConfirmClicked}
      onCancelClicked={onCancelClicked}
      isSaving={isSaving}
      hideDatasetSidebar={hideDatasetSidebar}
      onClearClicked={showClearButton ? onClearAll : undefined}
      applyOnChange={applyOnChange}
    />
  );
};

export default FieldPickerPopupContainer;
